/**
 * @module lib/commercial/keytar/globe-advisor/view-default
 */
import { setAfterLoginCookie, deleteUserCookies } from "lib/identity/api";
import tgamConfig from "config/tgam";
import consts from "lib/consts";
import logger from "utils/logger";
import {
  defaultWall,
  videoWall,
  previewTemplate,
  contentName,
  applyWallClickHandlers
} from "../common/preview";

const debugTag = "[KEYTAR]";
/**
 * The preview paywall to be displayed to authenticated users.
 * @param {String} contentType - The type of content that the template is being renderd within
 * @returns {String} of template HTML
 */
function authView(contentType) {
  return previewTemplate({
    style: "preview-template--globe-advisor",
    notice: `To access this ${contentName(contentType)}, you must be a Financial Advisor.`,
    logo: `
      <div class="o-primary-header o-primary-header--section">
        <h2 class="c-primary-title c-primary-title--section">Globe Advisor</h2>
      </div>`,
    body: `
      <h2>Get free access to the essential in-depth coverage, insight,
        analysis and tools you need to drive your business forward</h2>
      <p>Only Financial Advisors registered to Globe Advisor receive exclusive access to:</p>
      <ul>
        <li>All of Globe Advisor’s advisor-centric content, Market Update newsletters delivered
          throughout the trading day, and a weekly newsletter with the news you need.</li>
      </ul>`,
    actions: {
      cancel: {
        link: "/investing/globe-advisor/",
        text: "Close access notice and go to the Globe Advisor homepage",
        style: "c-button c-button--icon-only c-button--small preview-button__dismiss js-keytar-ga-close"
      },
      primary: {
        link: tgamConfig.keytar.globeAdvisorRegisterUrl,
        text: "Register",
        style: "c-button c-button--no-border c-button--full-width c-button--blue-secondary js-keytar-ga-register"
      },
      learn: {
        link: tgamConfig.keytar.globeAdvisorLearnMoreUrl,
        text: "Learn more",
        style: "js-keytar-ga-learn"
      }
    }
  });
}

/**
 * The preview paywall to be displayed to anonymous users.
 * @param {String} contentType - The type of content that the template is being renderd within
 * @returns {String} of template HTML
 */
function anonymousView(contentType) {
  return previewTemplate({
    style: "preview-template--globe-advisor",
    notice: `To access this ${contentName(contentType)}, you must first create a free Globe and Mail account.`,
    logo: `
      <div class="o-primary-header o-primary-header--section">
        <h2 class="c-primary-title c-primary-title--section">Globe Advisor</h2>
      </div>`,
    body: `
      <h2>Log in to your Globe and Mail account for a better reading experience,
        and to access your Advisor account.</h2>`,
    actions: {
      cancel: {
        link: "/investing/globe-advisor/",
        text: "Close access notice and go to the Globe Advisor homepage",
        style: "c-button c-button--icon-only c-button--small preview-button__dismiss js-keytar-ga-close"
      },
      primary: {
        link: consts.URLS.LOGIN,
        text: "Log in",
        style: "c-button c-button--no-border c-button--full-width c-button--blue-primary js-keytar-ga-login"
      },
      secondary: {
        link: consts.URLS.REGISTER.BASE,
        text: "Create an account",
        style: "c-button c-button--no-border c-button--full-width c-button--blue-secondary js-keytar-ga-create"
      }
    }
  });
}

/**
 * Renders a paywall
 * @param {String} contentType - The type of content the paywall is rendering within
 * @param {Object} wallAnalyticsSchema - An instance of the WallAnalyticsSchema class
 * @param {Boolean} isLoggedIn - true if user is authenticated (not anonymous)
 */
export default function init(contentType, wallAnalyticsSchema, isLoggedIn) {
  const wallName = "GA";
  let html = isLoggedIn ? authView : anonymousView;
  let error = null;

  if (contentType === "video" && !videoWall(html)) {
    error = "video powa container";
  } else {
    // Article pages use default configs so supply an empty object. Static Advisor CE Centre pages need to be handled a bit differently.
    const teaserWallConfigs = contentType !== "static" ? {}: {contentBodySelector: ".js-c-advisor-body", paragraphs: 5, isTeaserView: true, fadeBgColor: "stone-100"};

    if (!defaultWall(html, contentType, wallName, teaserWallConfigs)) {
      // Attempt to render wall template failed.  Set appropriate error message based on content type.
      error = `the ${contentType !=="static" ? "article" : "static"} content body"`;
    }
  }

  if (error) {
    logger.error(`${debugTag} Aborting - Globe Advisor Template cannot find ${error}`);
  } else {
    applyWallClickHandlers(wallAnalyticsSchema, "globe advisor paywall", link => {
      if (link.classList.contains("js-keytar-ga-login") || link.classList.contains("js-keytar-ga-create")) {
        deleteUserCookies();
      }
      if (!link.classList.contains("js-keytar-ga-close")) {
        setAfterLoginCookie();
      }
    });
  }
};
